// Canonical enums shared across job, contract, and review domains.

// JobStatus: lowercase, matches DB CHECK constraint on jobs.status
export type JobStatus =
  | 'pending'
  | 'processing'
  | 'completed'
  | 'failed'
  | 'cancelled';

// ReviewStatus: matches DB CHECK on jobs.review_status. The paused_at_*
// values are emitted by the per-stage HIL dispatcher; the original six are
// the queue/terminal states owned by the human-review (Gate 2) workflow.
export type PausedStage =
  | 'document_assistant'
  | 'document'
  | 'parties'
  | 'clause'
  | 'gate_1'
  | 'compliance'
  | 'signature'
  | 'gate_2'
  | 'gate'
  | 'orchestrator_bootstrap'
  // Legacy alias for any in-flight job paused at the old "orchestrator" stage.
  | 'orchestrator';

export type ReviewStatus =
  | 'pending_review'
  | 'claimed'
  | 'in_progress'
  | 'reviewed'
  | 'escalated'
  | 'returned'
  | 'paused_at_document_assistant'
  | 'paused_at_document'
  | 'paused_at_parties'
  | 'paused_at_clause'
  | 'paused_at_gate_1'
  | 'paused_at_compliance'
  | 'paused_at_signature'
  | 'paused_at_gate_2'
  | 'paused_at_gate'
  | 'paused_at_orchestrator_bootstrap'
  | 'paused_at_orchestrator';

export const STAGE_ORDER: PausedStage[] = [
  'document_assistant',
  'document',
  'parties',
  'clause',
  'gate_1',
  'compliance',
  'signature',
  'gate_2',
  'gate',
  'orchestrator_bootstrap',
];

export const STAGE_LABELS: Record<PausedStage, string> = {
  document_assistant: 'CMP Document Assistant — OCR verification',
  document: 'Document Agent',
  parties: 'Parties Agent',
  clause: 'Clause Agent',
  gate_1: 'Gate 1 — Quality Check',
  compliance: 'Compliance Agent',
  signature: 'Signature Agent',
  gate_2: 'Gate 2 — Quality Check',
  gate: 'Gate Agent — Final Synthesis',
  orchestrator_bootstrap: 'Orchestrator — Final Report',
  orchestrator: 'Orchestrator (legacy)',
};

export function pausedStage(rs: ReviewStatus | null | undefined): PausedStage | null {
  if (!rs || !rs.startsWith('paused_at_')) return null;
  const s = rs.slice('paused_at_'.length) as PausedStage;
  return (STAGE_ORDER as string[]).includes(s) ? s : null;
}

// FinalDecision: UPPERCASE 4-value human review outcome (jobs.final_decision)
export type FinalDecision =
  | 'APPROVED'
  | 'APPROVED_WITH_ALERTS'
  | 'REJECTED'
  | 'ESCALATED';

// GateDecision: UPPERCASE 5-value AI gate output (agent_results / gate report)
export type GateDecision =
  | 'APPROVED'
  | 'APPROVED_WITH_ALERTS'
  | 'REJECTED'
  | 'ESCALATED'
  | 'MANUAL_REVIEW_REQUIRED';

export type AnnotationAction = 'AGREE' | 'DISAGREE' | 'DISCUSS';

export type ContractLifecycleStep =
  | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 | 13 | 14 | 15;

/**
 * Mirrors core/auth/rbac.py and the profiles.role CHECK constraint.
 * Hierarchy: admin > senior_reviewer > reviewer > auditor > viewer.
 * `senior_reviewer` is the only non-admin role that may approve a job the gate
 * flagged with requires_senior_approval.
 */
export type UserRole = 'admin' | 'senior_reviewer' | 'reviewer' | 'viewer' | 'auditor';

// Matches backend IssueSeverity StrEnum (blocking / warning / info)
export type IssueSeverity = 'blocking' | 'warning' | 'info';
